/**
 * graphRuns.js — WHERE THIS AGENT'S OWN RUNS ARE, as drawing instructions.
 *
 * Founder, 2026-08-26: "can we do the same fiber effect on the normal graph as
 * well?? we did for the run worker but is it possible to do for others? when
 * the graph itself runs and travels between nodes??"
 *
 * A MEMBER satellite is a whole agent, so its mark comes from `activeRuns`. A
 * STEP is one node of THIS graph, so its mark comes from WHERE each of this
 * agent's runs currently is: `activeSteps` on the workflow row —
 * `[{executionId, step, from}]`, produced by the SAME backend pass that
 * produced `activeRuns` (backend `workflows.js countActiveRunsByWorkflow`). One
 * source: the header pill's count and the lights on the canvas are the same
 * rows, read once.
 *
 * `step` IS a graph node id, verbatim — the engine reports the key of its own
 * node map (agent-workflow `graph.ts`) and nothing renames it on the way here.
 * So the mapping below is an equality test, never a translation table.
 *
 * TWO PLACES, one contract — the self-host dashboard's twin is `graphRunMarks`
 * in `selfhosted/ui/index.html`, tripwired by its
 * `__tests__/ui-graph-running-steps.test.js`. Both answer the same question the
 * same way, because both draw the same picture.
 */

/**
 * @typedef {{step: string, from?: string}} RunAt
 */

/**
 * What to light, as pure data — no React, no DOM.
 *
 * @param {RunAt[]} steps      where this agent's runs are right now
 * @param {Object<string, true>} nodeIds  every node this canvas actually drew
 * @param {Object<string, string[]>} hopsInto  the lines drawn INTO each node,
 *        as `{targetId: [sourceId, …]}`; `''` is the bracket out of Start
 * @param {string} startId     the Start terminal's id ('' when none is drawn)
 * @returns {{nodes: Object<string, number>, hops: Object<string, number>}}
 *          `hops` is keyed `"<from>><to>"`, matching how the caller keys edges.
 */
export function graphRunMarks(steps, nodeIds = {}, hopsInto = {}, startId = '') {
  const out = { nodes: {}, hops: {} };
  for (const s of (Array.isArray(steps) ? steps : [])) {
    const to = (s && typeof s.step === 'string') ? s.step : '';
    if (!to) continue;
    if (!nodeIds[to]) {
      // Not a node of this graph. Almost always the synthetic `setup` step —
      // the run is installing its dependencies and has entered no authored step
      // yet — and otherwise a step from a graph version this page is not
      // showing. Either way the run IS running, so light the ENTRY POINT rather
      // than nothing: a dark canvas beside a "Running" header is exactly the
      // lie this exists to remove.
      if (startId && nodeIds[startId]) {
        out.nodes[startId] = (out.nodes[startId] || 0) + 1;
        // …AND the bracket out of Start. Founder (2026-08-27): "NEED FIBER
        // EFFECT ON THE LINE TOO WHEN STAGING" — a lit pill over a dark line
        // reads as stalled; the run is on its way INTO the first node, so the
        // line drawn with an empty origin carries the light. Every such line
        // (a graph may bracket several entry points), never a node-to-node one.
        // The bracket is drawn with an EMPTY origin when Start is the bare
        // pill, and with the Start NODE's id when the graph draws Start as a
        // real node — both are "the line out of Start".
        for (const t of Object.keys(hopsInto)) {
          for (const f of (hopsInto[t] || [])) {
            if (f === '' || f === startId) out.hops[`${f}>${t}`] = (out.hops[`${f}>${t}`] || 0) + 1;
          }
        }
      }
      continue;
    }
    out.nodes[to] = (out.nodes[to] || 0) + 1;
    const froms = hopsInto[to] || [];
    if (!froms.length) continue;
    const from = (s && typeof s.from === 'string') ? s.from : '';
    // WHICH incoming line to light — the LAST HOP, never the whole history:
    //   • the hop is drawn exactly as reported → that one line;
    //   • the origin is not a node of this graph (the synthetic `setup`, or the
    //     very first step, which reports no origin at all) → the bracket out of
    //     Start, which the caller keys with an empty origin;
    //   • neither (the serialized graph routes this hop through a synthesized
    //     `__branch` box, so the reported origin is two hops back) → every line
    //     into the node. Honest: work DID arrive here, and the canvas declines
    //     to claim which way it came.
    const lit = froms.includes(from) ? [from]
      : (!nodeIds[from] && froms.includes('') ? [''] : froms);
    for (const f of lit) {
      const k = `${f}>${to}`;
      out.hops[k] = (out.hops[k] || 0) + 1;
    }
  }
  return out;
}

/** The key a hop is stored under. ONE spelling for the marks and the edges. */
export function hopKey(from, to) {
  return `${from || ''}>${to || ''}`;
}

export default graphRunMarks;
